import React, { useEffect, useRef, useState } from "react";
import PhoneInput from "react-phone-number-input";
import { patienttitles } from "../../../../utils/title";
import baseUrl from "../../../../api/baseUrl";
import PersonIcon from "@mui/icons-material/Person";
import WcIcon from "@mui/icons-material/Wc";
import PhoneIcon from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";

function NewPatientAppointment({ formData, setformData }: any) {
  const [contactNumberStatus, setContactNumberStatus] = useState<null | "checking" | "exists" | "not_exists">(null);
  const [contactNumberFoundPatients, setContactNumberFoundPatients] = useState<any[]>([]);
  const contactNumberDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const contactNumberDropdownRef = useRef<HTMLDivElement>(null);

  const handlePhonenumber = (e: any) => {
    setformData({ ...formData, mobileNo: e });

    const raw = (e || "").toString().replace(/\D/g, "");
    if (contactNumberDebounceRef.current) clearTimeout(contactNumberDebounceRef.current);
    setContactNumberStatus(null);
    setContactNumberFoundPatients([]);
    if (raw.length < 5) return;

    setContactNumberStatus("checking");
    contactNumberDebounceRef.current = setTimeout(() => {
      baseUrl
        .post("client/patient/find-patients-by-phonenumber", { phonenumber: e })
        .then((res) => {
          if (!res.data.success || !Array.isArray(res.data.data)) {
            setContactNumberStatus("not_exists");
            setContactNumberFoundPatients([]);
            return;
          }
          const patients = res.data.data || [];
          setContactNumberFoundPatients(patients);
          setContactNumberStatus(patients.length > 0 ? "exists" : "not_exists");
        })
        .catch(() => {
          setContactNumberStatus("not_exists");
          setContactNumberFoundPatients([]);
        })
        .finally(() => {
          contactNumberDebounceRef.current = null;
        });
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (contactNumberDebounceRef.current) clearTimeout(contactNumberDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const el = contactNumberDropdownRef.current;
      if (el && !el.contains(event.target as Node)) {
        setContactNumberStatus(null);
        setContactNumberFoundPatients([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, []);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setformData((prevFormData: any) => ({ ...prevFormData, [name]: value }));
  };

  return (
    <div className="space-y-4">
      {/* Full Name */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
          <PersonIcon sx={{ fontSize: 18 }} className="text-indigo-500 dark:text-indigo-400" />
          Full Name <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-2">
          <select
            name="title"
            required={true}
            onChange={handleChange}
            value={formData.title}
            className="px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-slate-700 dark:text-slate-200"
          >
            <option value="">Title</option>
            {patienttitles.map((item: any, i: number) => (
              <option key={i} value={item.value}>
                {item.title}
              </option>
            ))}
          </select>
          <input
            type="text"
            name="fullName"
            onChange={handleChange}
            value={formData.fullName}
            placeholder="Enter full name"
            className="flex-1 px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
          />
        </div>
      </div>

      {/* Gender */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
          <WcIcon sx={{ fontSize: 18 }} className="text-indigo-500 dark:text-indigo-400" />
          Gender <span className="text-red-500">*</span>
        </label>
        <select
          name="gender"
          required={true}
          onChange={handleChange}
          value={formData.gender}
          className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-slate-700 dark:text-slate-200"
        >
          <option value="">Select Gender</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* Mobile No */}
      <div className="space-y-2 relative" ref={contactNumberDropdownRef}>
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
          <PhoneIcon sx={{ fontSize: 18 }} className="text-indigo-500 dark:text-indigo-400" />
          Mobile Number
        </label>
        <div className="[&_.PhoneInput]:bg-slate-50 dark:[&_.PhoneInput]:bg-slate-700 [&_.PhoneInput]:border [&_.PhoneInput]:border-slate-200 dark:[&_.PhoneInput]:border-slate-600 [&_.PhoneInput]:rounded-xl [&_.PhoneInput]:px-4 [&_.PhoneInput]:py-2 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:border-none [&_.PhoneInputInput]:text-slate-700 dark:[&_.PhoneInputInput]:text-slate-200">
          <PhoneInput
            international
            countryCallingCodeEditable={false}
            defaultCountry="LK"
            value={formData.mobileNo}
            onChange={handlePhonenumber}
          />
        </div>
        {contactNumberStatus && (
          <div className="absolute top-full left-0 right-0 z-10 mt-0.5 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg overflow-hidden">
            {contactNumberStatus === "checking" && (
              <div className="py-2 px-3 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2">
                <span className="w-3.5 h-3.5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                Checking...
              </div>
            )}
            {contactNumberStatus === "exists" && contactNumberFoundPatients.length > 0 && (
              <div className="max-h-48 overflow-y-auto py-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-slate-100 dark:[&::-webkit-scrollbar-track]:bg-slate-700 [&::-webkit-scrollbar-thumb]:bg-slate-300 dark:[&::-webkit-scrollbar-thumb]:bg-slate-500 [&::-webkit-scrollbar-thumb]:rounded-full">
                <p className="px-3 py-1.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide">
                  Already registered
                </p>
                {contactNumberFoundPatients.map((patient: any, index: number) => (
                  <div
                    key={patient._id || index}
                    className="px-3 py-2 border-t border-slate-100 dark:border-slate-600 first:border-t-0"
                  >
                    <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">
                      {patient.fullName || "—"}
                    </p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-slate-600 dark:text-slate-400">
                      {patient.regNo != null && patient.regNo !== "" && (
                        <span>Reg: {patient.regNo}</span>
                      )}
                      {patient.mobileNo && (
                        <span>{patient.mobileNo}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {contactNumberStatus === "not_exists" && (
              <div className="py-2 px-3 text-xs text-green-600 dark:text-green-400 font-medium">
                Available
              </div>
            )}
          </div>
        )}
      </div>

      {/* Date of Birth */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
          <CalendarTodayIcon sx={{ fontSize: 18 }} className="text-indigo-500 dark:text-indigo-400" />
          Date of Birth
        </label>
        <input
          type="date"
          name="dob"
          value={formData.dob}
          onChange={handleChange}
          className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-slate-700 dark:text-slate-200"
        />
      </div>

      {/* Address */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
          <LocationOnIcon sx={{ fontSize: 18 }} className="text-indigo-500 dark:text-indigo-400" />
          Address
        </label>
        <input
          type="text"
          name="address"
          value={formData.address}
          onChange={handleChange}
          placeholder="Enter address"
          className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
        />
      </div>
    </div>
  );
}

export default NewPatientAppointment;
